<template>
  <v-app id="app_sales_management">
    <v-container fluid>
      <v-row class="align-center mt-2 mb-4">
        <v-col cols="8">
          <p style="color: #9e9e9e; font-size: 22px; font-weight: 700; margin-bottom: 2px; text-transform: uppercase;">SUMMARY REPORT</p>
          <p style="color: #9e9e9e; font-size: 14px; margin-bottom: 0;">Daily report analysis for agents utilizing various parameters.</p>
        </v-col>
        <v-col cols="4" class="d-flex justify-end align-center px-4">
          <v-divider vertical style="border-right-width: 2px; border-color: #e0e0e0; max-height: 40px; margin-right: 15px; opacity: 1;"></v-divider>
          <v-btn
            variant="text"
            style="
              border-radius: 2px;
              font-weight: 600;
              letter-spacing: 0.5px;
              color: #111;
              font-size: 19px;
            "
            @click="backPageRoute()"
          >
            <v-icon size="large" class="mr-1">mdi-menu-left</v-icon>
            BACK
          </v-btn>
        </v-col>
      </v-row>

      <v-card class="custom_card_box mt-4" elevation="1" border="sm opacity-1" rounded="lg">
        <v-card-text class="custom_tool_bar_style">
          <v-btn class="custom_pdf_btn" variant="flat" size="large" @click="exportPDF" :disabled="loading">
            <v-icon size="large">mdi-file-pdf-box</v-icon>&nbsp;&nbsp;PDF
          </v-btn>
          <v-btn class="custom_excel_btn ml-4" variant="flat" size="large" @click="exportExcel" :disabled="loading">
            <v-icon size="large">mdi-file-excel-box</v-icon>&nbsp;&nbsp;EXCEL
          </v-btn>
        </v-card-text>

        <v-divider thickness="2"></v-divider>

        <v-expansion-panels v-model="panelOpen" multiple>
          <v-expansion-panel value="filters" elevation="0">
            <v-expansion-panel-title expand-icon="mdi-menu-down">
              <v-icon class="mr-3">mdi-filter-menu-outline</v-icon>
              <span class="text-body-1 font-weight-bold">FILTERS</span>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-row class="mt-2" dense>
                <v-col cols="12" md="4" class="py-1">
                  <v-menu
                    v-model="showDatePicker"
                    :close-on-content-click="false"
                    location="bottom start"
                    transition="scale-transition"
                  >
                    <template v-slot:activator="{ props }">
                      <v-text-field
                        v-model="dateRangeText"
                        label="Select Date Range"
                        placeholder="Click to select date range"
                        variant="outlined"
                        density="comfortable"
                        class="custom_input_field_color"
                        readonly
                        v-bind="props"
                        :loading="loading"
                        clearable
                        @click:clear="clearDateRange"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon>mdi-calendar-range</v-icon>
                        </template>
                      </v-text-field>
                    </template>

                    <v-card class="custom_calendar_dropdown" elevation="8" rounded="lg">
                      <div class="custom_calendar_header">
                        <div class="custom_calendar_month_select">
                          {{ monthNames[viewMonth] }} {{ viewYear }}
                          <v-icon size="small" class="ml-1">mdi-menu-down</v-icon>
                        </div>
                        <div class="custom_calendar_nav">
                          <v-btn icon variant="text" size="small" @click="prevMonth">
                            <v-icon>mdi-chevron-left</v-icon>
                          </v-btn>
                          <v-btn icon variant="text" size="small" @click="nextMonth">
                            <v-icon>mdi-chevron-right</v-icon>
                          </v-btn>
                        </div>
                      </div>

                      <div class="custom_calendar_weekdays">
                        <span v-for="(d, i) in weekDays" :key="i">{{ d }}</span>
                      </div>

                      <div class="custom_calendar_grid">
                        <button
                          v-for="(cell, idx) in calendarCells"
                          :key="idx"
                          type="button"
                          class="custom_calendar_day"
                          :class="{
                            'is-other-month': cell.otherMonth,
                            'is-today': cell.isToday,
                            'is-in-range': cell.isInRange,
                            'is-range-start': cell.isRangeStart,
                            'is-range-end': cell.isRangeEnd
                          }"
                          :disabled="cell.otherMonth"
                          @click="selectCalendarDate(cell)"
                        >
                          {{ cell.day }}
                        </button>
                      </div>

                      <v-divider></v-divider>

                      <div class="custom_calendar_actions">
                        <v-btn variant="text" class="custom_calendar_cancel_btn" @click="cancelDateSelection">CANCEL</v-btn>
                        <v-btn variant="text" class="custom_calendar_ok_btn" @click="applyDateRange">OK</v-btn>
                      </div>
                    </v-card>
                  </v-menu>
                </v-col>

                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectDistrict"
                    :items="districtOptions"
                    label="Area"
                    placeholder="Select area"
                    variant="outlined"
                    density="comfortable"
                    multiple
                    class="custom_input_field_color"
                    clearable
                    :loading="loading"
                  >
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                      >
                        {{ item.title }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" md="4" class="py-1">
                  <v-autocomplete
                    v-model="selectedAgent"
                    :items="agentOptions"
                    label="Sales Rep"
                    placeholder="Select sales rep name"
                    variant="outlined"
                    density="comfortable"
                    multiple
                    class="custom_input_field_color"
                    clearable
                    :loading="loading"
                  >
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#e7e7e7"
                        variant="flat"
                        class="text-black font-weight-medium"
                        size="small"
                        closable
                      >
                        {{ item.title }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" md="4" class="py-1">
                  <v-text-field
                    v-model="search"
                    clearable
                    label="Search"
                    placeholder="Search by Sales Rep, Area, or Date"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    prepend-inner-icon="mdi-magnify"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-divider class="mb-4 mt-2"></v-divider>

        <v-card-text class="pt-0">
          <v-row v-if="error" justify="center" class="py-8">
            <v-col cols="12" md="8" class="text-center">
              <v-icon size="64" color="error">mdi-alert-circle</v-icon>
              <p class="text-center mt-4 text-error font-weight-bold">{{ error }}</p>
              <v-btn color="primary" variant="flat" @click="fetchSummaryReports" class="mt-4">
                <v-icon left>mdi-refresh</v-icon>
                Retry
              </v-btn>
            </v-col>
          </v-row>

          <v-row v-else-if="loading" justify="center" class="py-8">
            <v-col cols="auto">
              <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
              <p class="text-center mt-4">Loading summary reports...</p>
            </v-col>
          </v-row>

          <v-data-table
            v-else
            :headers="headers"
            :items="filteredSummaryReports"
            :items-per-page="10"
            :loading="loading"
            :no-data-text="'No summary reports found'"
            :footer-props="{
              'items-per-page-text': 'Rows per page:',
              'show-current-page': true,
              'show-first-last-page': true
            }"
          >
            <template v-slot:[`item.sales_rep`]="{ item }">
              <div class="py-3">
                <div class="text-body-2 text-black">{{ item.saleRep_name || '—' }}</div>
                <div class="text-caption" style="color: #001242;">Code: {{ item.saleRepCode || '—' }}</div>
              </div>
            </template>

            <template v-slot:[`item.date`]="{ item }">
              <span class="text-body-2">{{ formatDate(item.date) }}</span>
            </template>

            <template v-slot:[`item.day_in`]="{ item }">
              <div class="d-flex align-center">
                <v-icon color="green" size="10" class="mr-2">mdi-circle</v-icon>
                <span class="text-body-2">{{ formatTime(item.dayInTime) }}</span>
              </div>
            </template>

            <template v-slot:[`item.day_out`]="{ item }">
              <div class="d-flex align-center">
                <v-icon color="red-lighten-2" size="10" class="mr-2">mdi-circle</v-icon>
                <span class="text-body-2">{{ formatTime(item.dayOutTime) }}</span>
              </div>
            </template>

            <template v-slot:[`item.area`]="{ item }">
              <span class="text-body-2">{{ item.area_code || '—' }}</span>
            </template>

            <template v-slot:[`item.working_hours`]="{ item }">
              <span class="text-body-2">{{ item.totalWorkingHours }} hrs</span>
            </template>

            <template v-slot:[`item.action`]="{ item }">
              <v-menu transition="slide-x-transition">
                <template v-slot:activator="{ props }">
                  <v-btn class="custom_btn_primary" variant="flat" size="large" v-bind="props">
                    ACTIONS &nbsp; &nbsp;<v-icon>mdi-menu-down</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item v-if="permissions.read" @click="viewItem(item)">
                    <v-list-item-title class="custom_font_color_view">VIEW</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="permissions.delete" @click="confirmDelete(item)">
                    <v-list-item-title class="custom_font_color_delete">DELETE</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>

          <v-row class="mt-2" v-if="!loading && !error">
            <v-col cols="12">
              <div class="text-caption text-center text-grey">
                Showing {{ filteredSummaryReports.length }} of {{ summaryReports.length }} entries
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-dialog v-model="view_Dialog" fullscreen>
        <v-card flat>
          <v-toolbar style="border-radius: 0px !important;"
                    class="custom_fullscreen_dialog_toolbar pr-5 custom_card_box" color="white" dense>
            <v-toolbar-title>
              <v-img class="custom_company_logo_nav" :src="require('@/img/company_logo/logo-dark 2.png')"
                    @click="homeReload()"></v-img>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn style="background-color: #800080; color: white; border-radius: 2px;" variant="flat" @click="view_Dialog = false">
              CLOSE
            </v-btn>
          </v-toolbar>

          <v-row class="mt-15 custom_scroll_option_fullscreen_dialog">
            <v-col cols="12" md="12">
              <Sales_Record :summaryId="selectedSummaryId"/>
            </v-col>
          </v-row>
        </v-card>
      </v-dialog>

      <v-dialog v-model="deleteDialog" max-height="500" max-width="500" persistent>
        <v-card rounded="md">
          <div class="pa-4">
            <v-card color="#F0F0F0" flat>
              <template v-slot:prepend>
                <v-card-title class="text-subtitle-1 font-weight-bold">DELETE DATA</v-card-title>
              </template>
              <v-spacer></v-spacer>
              <template v-slot:append>
                <v-btn @click="deleteDialog = false" icon="mdi-close" size="x-small"></v-btn>
              </template>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <v-card-item>
                <v-card-title style="color: #BB0000" class="text-body-1 font-weight-medium">
                  <v-icon color="warning" size="small">mdi-alert</v-icon>
                  &nbsp;&nbsp;Do you want to delete this record ?
                </v-card-title>

                <p style="opacity: 0.7;" class="text-body-2 mt-4 text-justify">
                  This record will be deleted immediately. Once deleted, it can no longer be used for any purpose.
                </p>

                <v-divider class="mt-4"></v-divider>

                <div class="float-end mt-4">
                  <v-btn class="custom_btn_primary" style="border-radius: 2px;" @click="deleteItem" :loading="deleting">DELETE</v-btn>
                  <v-btn class="ml-3 custom_btn_secondary" style="border-radius: 2px;" @click="deleteDialog = false">CANCEL</v-btn>
                </div>
              </v-card-item>
            </v-card>
          </div>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackAlert.snackbar" :color="snackAlert.snackColor" location="top right" :timeout="3000">
        {{ snackAlert.snackMessage }}
      </v-snackbar>
    </v-container>
  </v-app>
</template>

<script>
import Sales_Record from "@/components/SALES_MANAGEMENT/Sales_Record.vue";
import { mapActions } from "vuex";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from "xlsx";
import { getPermissions } from "@/plugins/permissions";
import axios from 'axios';

export default {
  components: { Sales_Record },
  data() {
    return {
      panelOpen: ['filters'],
      snackAlert: { snackbar: false, snackColor: "", snackMessage: "" },
      summaryReports: [],
      loading: false,
      deleting: false,
      error: null,
      permissions: {},

      currentPage: 1,
      perPage: 1000,
      lastPage: 1,
      totalRecords: 0,
      fromRecord: 0,
      toRecord: 0,
      paginationInfo: null,

      dates: [],
      showDatePicker: false,
      dateRangeText: '',
      rangeStart: null,
      rangeEnd: null,
      viewYear: new Date().getFullYear(),
      viewMonth: new Date().getMonth(),
      weekDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],

      search: '',
      selectedAgent: [],
      selectDistrict: [],
      agentOptions: [],
      districtOptions: [],

      deleteDialog: false,
      itemToDelete: null,
      view_Dialog: false,
      selectedSummaryId: null,

      cancelTokenSource: null,

      headers: [
        { title: 'SALES REP NAME', key: 'sales_rep', align: 'start', sortable: false },
        { title: 'DATE', key: 'date', align: 'start', sortable: true },
        { title: 'DAY IN', key: 'day_in', align: 'start', sortable: false },
        { title: 'DAY OUT', key: 'day_out', align: 'start', sortable: false },
        { title: 'AREA CODE', key: 'area', align: 'start', sortable: false },
        { title: 'WORKING HOURS', key: 'working_hours', align: 'start', sortable: true },
        { title: 'ACTIONS', key: 'action', sortable: false, align: 'start' },
      ],
    }
  },

  computed: {
    calendarCells() {
      const cells = [];
      const firstDayOfMonth = new Date(this.viewYear, this.viewMonth, 1);
      const startWeekday = firstDayOfMonth.getDay();
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (let i = 0; i < startWeekday; i++) {
        cells.push({ day: '', otherMonth: true, date: null });
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const cellDate = new Date(this.viewYear, this.viewMonth, day);
        cellDate.setHours(0, 0, 0, 0);
        const isToday = cellDate.getTime() === today.getTime();

        let isRangeStart = false;
        let isRangeEnd = false;
        let isInRange = false;

        if (this.rangeStart) {
          isRangeStart = cellDate.getTime() === this.rangeStart.getTime();
        }
        if (this.rangeEnd) {
          isRangeEnd = cellDate.getTime() === this.rangeEnd.getTime();
        }
        if (this.rangeStart && this.rangeEnd) {
          isInRange = cellDate.getTime() > this.rangeStart.getTime() && cellDate.getTime() < this.rangeEnd.getTime();
        }

        cells.push({ day, otherMonth: false, date: cellDate, isToday, isRangeStart, isRangeEnd, isInRange });
      }

      while (cells.length % 7 !== 0) {
        cells.push({ day: '', otherMonth: true, date: null });
      }

      return cells;
    },

    filteredSummaryReports() {
      let data = this.summaryReports;

      if (this.selectDistrict && this.selectDistrict.length > 0) {
        data = data.filter(item => this.selectDistrict.includes(item.area_code));
      }

      if (this.selectedAgent && this.selectedAgent.length > 0) {
        data = data.filter(item => this.selectedAgent.includes(item.saleRep_name));
      }

      if (this.dates && this.dates.length === 2) {
        const from = this.formatDateForAPI(this.dates[0]);
        const to = this.formatDateForAPI(this.dates[1]);
        data = data.filter(item => {
          const itemDate = this.formatDateForAPI(item.date);
          return itemDate && from && to && itemDate >= from && itemDate <= to;
        });
      }

      if (this.search && this.search.trim().length > 0) {
        const searchTerm = this.search.trim().toLowerCase();
        data = data.filter(item => {
          const rep = (item.saleRep_name || '').toLowerCase();
          const area = (item.area_code || '').toLowerCase();
          const dateStr = this.formatDate(item.date).toLowerCase();
          return rep.includes(searchTerm) || area.includes(searchTerm) || dateStr.includes(searchTerm);
        });
      }

      return data;
    },
  },

  watch: {
    showDatePicker(val) {
      if (val) {
        if (this.rangeStart) {
          this.viewYear = this.rangeStart.getFullYear();
          this.viewMonth = this.rangeStart.getMonth();
        } else {
          const now = new Date();
          this.viewYear = now.getFullYear();
          this.viewMonth = now.getMonth();
        }
      }
    },

    view_Dialog(val) {
      if (!val) {
        sessionStorage.removeItem('reloaded_view_summary_id');
      }
    }
  },

  methods: {
    ...mapActions(['updateSharedData']),

    getAuthToken() {
      return localStorage.getItem('token') ||
             localStorage.getItem('access_token') ||
             sessionStorage.getItem('token') ||
             sessionStorage.getItem('access_token');
    },

    getBaseURL() {
      return process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_API_BASE_URL;
    },

    homeReload() {
      this.$router.push('/dashboard');
    },

    formatDateForAPI(dateString) {
      if (!dateString) return null;

      if (dateString instanceof Date) {
        const year = dateString.getFullYear();
        const month = String(dateString.getMonth() + 1).padStart(2, '0');
        const day = String(dateString.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      if (typeof dateString === 'string') {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }

      return null;
    },

    updateDateRangeText() {
      if (this.dates && this.dates.length === 2) {
        const start = this.formatDate(this.dates[0]);
        const end = this.formatDate(this.dates[1]);
        this.dateRangeText = `${start} → ${end}`;
      } else {
        this.dateRangeText = '';
      }
    },

    prevMonth() {
      if (this.viewMonth === 0) {
        this.viewMonth = 11;
        this.viewYear -= 1;
      } else {
        this.viewMonth -= 1;
      }
    },

    nextMonth() {
      if (this.viewMonth === 11) {
        this.viewMonth = 0;
        this.viewYear += 1;
      } else {
        this.viewMonth += 1;
      }
    },

    selectCalendarDate(cell) {
      if (cell.otherMonth || !cell.date) return;

      if (!this.rangeStart || (this.rangeStart && this.rangeEnd)) {
        this.rangeStart = cell.date;
        this.rangeEnd = null;
      } else if (cell.date.getTime() < this.rangeStart.getTime()) {
        this.rangeEnd = this.rangeStart;
        this.rangeStart = cell.date;
      } else {
        this.rangeEnd = cell.date;
      }
    },

    cancelDateSelection() {
      this.showDatePicker = false;
    },

    applyDateRange() {
      if (this.rangeStart && this.rangeEnd) {
        this.dates = [this.rangeStart, this.rangeEnd];
      } else if (this.rangeStart && !this.rangeEnd) {
        this.dates = [this.rangeStart, this.rangeStart];
      } else {
        this.dates = [];
      }
      this.updateDateRangeText();
      this.showDatePicker = false;
    },

    clearDateRange() {
      this.dates = [];
      this.rangeStart = null;
      this.rangeEnd = null;
      this.dateRangeText = '';
      this.showDatePicker = false;
    },

    async fetchSummaryReports() {
      if (this.cancelTokenSource) {
        console.log('🛑 Cancelling previous request...');
        this.cancelTokenSource.cancel('New search initiated');
      }

      this.cancelTokenSource = axios.CancelToken.source();
      this.loading = true;
      this.error = null;

      try {
        const token = this.getAuthToken();
        const baseURL = this.getBaseURL();

        const params = {
          page: 1,
          perPage: this.perPage,
        };

        console.log('📤 Summary Report API Parameters:', params);

        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await axios.get(`${baseURL}/api/daily-summary/list`, {
          params,
          cancelToken: this.cancelTokenSource.token,
          headers,
          timeout: 30000
        });

        console.log('📥 API Response:', response.data);

        if (response.data && response.data.success === true) {
          this.summaryReports = response.data.data || [];

          if (response.data.pagination) {
            this.currentPage = response.data.pagination.current_page;
            this.lastPage = response.data.pagination.last_page;
            this.totalRecords = response.data.pagination.total;
            this.perPage = response.data.pagination.per_page;
            this.fromRecord = response.data.pagination.from;
            this.toRecord = response.data.pagination.to;
            this.paginationInfo = response.data.pagination;
          }

          this.updateFilterOptions(this.summaryReports);

          const savedViewId = sessionStorage.getItem('reloaded_view_summary_id');
          if (!this.view_Dialog && !savedViewId) {
            this.snackAlert = {
              snackbar: true,
              snackColor: this.summaryReports.length ? 'success' : 'info',
              snackMessage: this.summaryReports.length
                ? `${this.summaryReports.length} report(s) loaded successfully`
                : 'No reports found'
            };
          }
        } else {
          throw new Error(response.data?.message || 'Failed to fetch reports');
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('⚠️ Request cancelled:', error.message);
        } else {
          console.error('Error fetching summary reports:', error);

          if (error.code === 'ECONNABORTED') {
            this.error = 'Request timeout. Please check your connection and try again.';
          } else if (error.response?.status === 401) {
            this.error = 'Authentication required. Please login to view reports.';
          } else if (error.response?.status === 403) {
            this.error = 'You do not have permission to view these reports.';
          } else if (error.response?.status === 404) {
            this.error = 'API endpoint not found. Please check your API configuration.';
          } else if (error.response?.status === 500) {
            this.error = 'Server error. Please try again later.';
          } else {
            this.error = error.response?.data?.message || error.message || 'Failed to load summary reports';
          }

          this.snackAlert = { snackbar: true, snackColor: 'error', snackMessage: this.error };
        }
      } finally {
        this.loading = false;
      }
    },

    updateFilterOptions(reportsData) {
      const agents = new Set();
      const districts = new Set();

      reportsData.forEach(item => {
        const agentName = item.saleRep_name || item.agent_name || item.salesRep_name;
        if (agentName) agents.add(agentName);

        const districtCode = item.area_code || item.district_code || item.area || item.district;
        if (districtCode) districts.add(districtCode);
      });

      this.agentOptions = Array.from(agents).sort();
      this.districtOptions = Array.from(districts).sort();
    },

    async deleteSummaryReport(id) {
      this.deleting = true;
      try {
        const token = this.getAuthToken();
        const baseURL = this.getBaseURL();

        const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await axios.delete(`${baseURL}/api/daily-summary/delete/${id}`, { headers });

        if (response.data && response.data.success === true) {
          await this.fetchSummaryReports();
          this.snackAlert = { snackbar: true, snackColor: 'success', snackMessage: 'Record deleted successfully' };
        } else {
          throw new Error(response.data?.message || 'Failed to delete record');
        }
      } catch (error) {
        console.error('Error deleting summary report:', error);
        this.snackAlert = {
          snackbar: true,
          snackColor: 'error',
          snackMessage: error.response?.data?.message || 'Failed to delete record'
        };
      } finally {
        this.deleting = false;
      }
    },

    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    async deleteItem() {
      if (this.itemToDelete) {
        await this.deleteSummaryReport(this.itemToDelete.id);
        this.deleteDialog = false;
        this.itemToDelete = null;
      }
    },

    viewItem(item) {
      this.selectedSummaryId = item.id;
      this.updateSharedData(item.id);
      this.view_Dialog = true;
      sessionStorage.setItem('reloaded_view_summary_id', item.id);
    },

    backPageRoute() {
      this.$router.push('/dashboard');
    },

    resetFilters() {
      this.dates = [];
      this.rangeStart = null;
      this.rangeEnd = null;
      this.dateRangeText = '';
      this.search = '';
      this.selectedAgent = [];
      this.selectDistrict = [];
      this.snackAlert = { snackbar: true, snackColor: 'info', snackMessage: 'All filters have been reset' };
    },

    exportPDF() {
      const dataToExport = this.filteredSummaryReports;

      if (dataToExport.length === 0) {
        this.snackAlert = { snackbar: true, snackColor: 'warning', snackMessage: 'No data to export' };
        return;
      }

      const doc = new jsPDF();
      doc.text('Summary Report', 14, 10);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 18);

      const tableData = dataToExport.map(item => [
        `${item.saleRep_name || '—'} (${item.saleRepCode || '—'})`,
        this.formatDate(item.date),
        this.formatTime(item.dayInTime),
        this.formatTime(item.dayOutTime),
        `${item.totalWorkingHours || 0} hrs`
      ]);

      doc.autoTable({
        head: [['Sales Rep', 'Date', 'Day In', 'Day Out', 'Working Hours']],
        body: tableData,
        startY: 25,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [79, 70, 229] }
      });

      doc.save('summary_report.pdf');
    },

    exportExcel() {
      const dataToExport = this.filteredSummaryReports;

      if (dataToExport.length === 0) {
        this.snackAlert = { snackbar: true, snackColor: 'warning', snackMessage: 'No data to export' };
        return;
      }

      const worksheetData = dataToExport.map(item => ({
        'Sales Rep Name': item.saleRep_name || '—',
        'Sales Rep Code': item.saleRepCode || '—',
        'Date': this.formatDate(item.date),
        'Day In Time': this.formatTime(item.dayInTime),
        'Day Out Time': this.formatTime(item.dayOutTime),
        'Working Hours': item.totalWorkingHours || 0,
        'Total Collection': this.formatCurrency(item.totalCollection || 0),
        'Cash Amount': this.formatCurrency(item.cashAmount || 0),
        'Cheque Amount': this.formatCurrency(item.chequeAmount || 0),
        'Bank Amount': this.formatCurrency(item.bankAmount || 0),
        'Created At': this.formatDateTime(item.created_at),
      }));

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Summary Report');

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      XLSX.writeFile(workbook, `summary_report_${timestamp}.xlsx`);

      this.snackAlert = {
        snackbar: true,
        snackColor: 'success',
        snackMessage: `${dataToExport.length} records exported successfully`
      };
    },

    formatDate(dateString) {
      if (!dateString) return '—';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
      } catch (e) {
        return dateString;
      }
    },

    formatTime(timeString) {
      if (!timeString) return '—';
      try {
        if (timeString.includes(':')) {
          const parts = timeString.split(':');
          if (parts.length >= 2) {
            const hours = parseInt(parts[0]);
            const minutes = parts[1];
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const hour12 = hours % 12 || 12;
            return `${hour12}:${minutes} ${ampm}`;
          }
        }
        return timeString;
      } catch (e) {
        return timeString;
      }
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '—';
      try {
        const date = new Date(dateTimeString);
        if (isNaN(date.getTime())) return dateTimeString;
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return dateTimeString;
      }
    },

    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '0.00';
      const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
      if (isNaN(numAmount)) return '0.00';
      return numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },

  mounted() {
    this.permissions = getPermissions(11, "Module Name");
    if (!this.permissions.read) {
      this.snackAlert = {
        snackbar: true,
        snackColor: 'error',
        snackMessage: "Access Denied - You don't have permission to view this page"
      };
      this.$router.push('/dashboard');
    } else {
      this.fetchSummaryReports();

      const savedViewId = sessionStorage.getItem('reloaded_view_summary_id');
      if (savedViewId) {
        this.selectedSummaryId = isNaN(savedViewId) ? savedViewId : Number(savedViewId);
        this.updateSharedData(this.selectedSummaryId);
        this.view_Dialog = true;
      }
    }
  },

  beforeUnmount() {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel('Component unmounting');
    }
  },
}
</script>

<style lang="scss" scoped>
  @use "../../styles/sales_management/sales_management.scss" as *;

  .custom_fullscreen_dialog_toolbar {
    position: fixed !important;
    z-index: 999999 !important;
  }

  .custom_company_logo_nav {
    width: 110px;
    height: 35px;
    cursor: pointer;
  }

  .custom_card_box {
    font-family: var(--font-family) !important;
    border-top-left-radius: var(--border_top_left_radius) !important;
    border-top-right-radius: var(--border_top_right_radius) !important;
    border-bottom-right-radius: var(--border_bottom_right_radius) !important;
    box-shadow: 0px 4px 18px 0px #0000001F !important;
    background-color: #ffffff;
  }

  .filter-active {
    background-color: rgba(79, 70, 229, 0.1);
    border-left: 3px solid #4f46e5;
  }

  .filter-stats {
    font-size: 0.875rem;
    color: #6b7280;
    margin-left: 1rem;
  }

  .custom_calendar_dropdown {
    width: 320px;
    padding: 16px;
    background-color: #ffffff;
  }

  .custom_calendar_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .custom_calendar_month_select {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
  }

  .custom_calendar_nav {
    display: flex;
    align-items: center;
  }

  .custom_calendar_weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 8px;
  }

  .custom_calendar_grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    row-gap: 4px;
  }

  .custom_calendar_day {
    border: none;
    background: transparent;
    width: 36px;
    height: 36px;
    margin: 0 auto;
    font-size: 14px;
    color: #1a1a1a;
    border-radius: 50%;
    cursor: pointer;
  }

  .custom_calendar_day.is-other-month {
    visibility: hidden;
    cursor: default;
  }

  .custom_calendar_day.is-today {
    border: 2px solid #001242;
    color: #001242;
    font-weight: 700;
  }

  .custom_calendar_day.is-in-range {
    background-color: rgba(0, 18, 66, 0.08);
    border-radius: 0;
  }

  .custom_calendar_day.is-range-start,
  .custom_calendar_day.is-range-end {
    background-color: #162f74;
    color: #ffffff;
    font-weight: 700;
  }

  .custom_calendar_actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 8px;
    gap: 8px;
  }

  .custom_calendar_cancel_btn {
    color: #9e9e9e;
    font-weight: 600;
  }

  .custom_calendar_ok_btn {
    color: #162f74;
    font-weight: 600;
  }
</style>